import { useEffect, useState } from "react";
import { ExternalLink, CheckCircle2, Clock, XCircle, Copy, Check, Search } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

/**
 * DirectorySubmissions — admin-only checklist of high-authority industry
 * directories for behavioral-health orgs to submit to.
 *
 * Each row's `status` + `notes` are persisted per-admin as a JSON blob on the
 * admin's own user record (via /admin/directories/status). We keep this
 * client-heavy on purpose — the *value* is the curated list; the persistence
 * layer is just so the checkboxes survive a reload.
 */

const DIRECTORIES = [
  { name: "Psychology Today", url: "https://www.psychologytoday.com/us/join", category: "Therapist Directory", priority: "P0",
    notes: "The single highest-traffic BH directory. Individual clinician profiles get 20-50k monthly visits." },
  { name: "SAMHSA Behavioral Health Treatment Locator", url: "https://findtreatment.gov/", category: "Federal Directory", priority: "P0",
    notes: "Federal listing — free. Adds credibility, no fee." },
  { name: "Rehabs.com", url: "https://www.rehabs.com/business-listing/", category: "Treatment Directory", priority: "P0",
    notes: "Owned by American Addiction Centers. High DA. Free basic listing available." },
  { name: "Recovery.com (formerly Rehab.com)", url: "https://recovery.com/business/", category: "Treatment Directory", priority: "P0",
    notes: "Modern UI, good conversion. Free tier + paid featured slot." },
  { name: "NABH (National Association for Behavioral Healthcare)", url: "https://www.nabh.org/membership/", category: "Trade Association", priority: "P1",
    notes: "Membership required (paid) — but confers backlink + industry credibility." },
  { name: "The Joint Commission — Quality Check", url: "https://www.qualitycheck.org/", category: "Accreditor Directory", priority: "P1",
    notes: "Requires TJC accreditation. Automatic listing once accredited." },
  { name: "CARF — Provider Search", url: "https://www.carf.org/providerSearch.aspx", category: "Accreditor Directory", priority: "P1",
    notes: "Requires CARF accreditation. Automatic once accredited." },
  { name: "GoodTherapy.org", url: "https://www.goodtherapy.org/join.html", category: "Therapist Directory", priority: "P1",
    notes: "Requires 2+ years post-license. Good editorial content SEO." },
  { name: "TherapyDen", url: "https://www.therapyden.com/for-therapists", category: "Therapist Directory", priority: "P2",
    notes: "Free directory, growing audience. Values inclusive-provider language." },
  { name: "Zencare", url: "https://blog.zencare.co/therapists/", category: "Therapist Directory", priority: "P2",
    notes: "Curated network; each listing manually reviewed." },
  { name: "AllTreatment.com", url: "https://www.alltreatment.com/", category: "Treatment Directory", priority: "P2",
    notes: "Free listing available." },
  { name: "Addiction Center", url: "https://www.addictioncenter.com/treatment-centers/", category: "Treatment Directory", priority: "P2",
    notes: "High DA. Contact sales for listing." },
  { name: "ASAM Provider Directory", url: "https://www.asam.org/asam-criteria", category: "Clinical Association", priority: "P2",
    notes: "For addiction-medicine specialists. Requires ASAM membership." },
  { name: "IAEDP (Eating Disorders)", url: "https://www.iaedp.com/", category: "Specialty Association", priority: "P2",
    notes: "Only if you serve ED populations." },
  { name: "NAMI Local Chapter", url: "https://www.nami.org/find-your-local-nami/", category: "Advocacy", priority: "P2",
    notes: "Contact your state/local NAMI chapter for provider listing + referrals." },
  { name: "Google Business Profile", url: "https://www.google.com/business/", category: "Local SEO", priority: "P0",
    notes: "Essential for local search. Verify with a postcard." },
  { name: "Bing Places", url: "https://www.bingplaces.com/", category: "Local SEO", priority: "P1",
    notes: "Bing's equivalent of GBP. 5-10% of US search traffic." },
  { name: "Apple Business Connect", url: "https://businessconnect.apple.com/", category: "Local SEO", priority: "P1",
    notes: "Shows in Apple Maps, Siri, Spotlight. Growing importance." },
  { name: "LinkedIn Company Page", url: "https://www.linkedin.com/company/setup/new/", category: "Social", priority: "P0",
    notes: "Foundational for B2B credibility and hiring." },
  { name: "Better Business Bureau", url: "https://www.bbb.org/get-accredited", category: "Trust Signal", priority: "P2",
    notes: "Paid accreditation. Older-demographic clientele trust the seal." },
];

const STATUSES = [
  { key: "not_started", label: "Not started", icon: <Clock size={12} />, color: "text-[var(--muted)] border-[var(--line)]" },
  { key: "submitted", label: "Submitted", icon: <Clock size={12} />, color: "text-amber-800 border-amber-300 bg-amber-50" },
  { key: "live", label: "Live", icon: <CheckCircle2 size={12} />, color: "text-emerald-800 border-emerald-400 bg-emerald-50" },
  { key: "rejected", label: "Rejected", icon: <XCircle size={12} />, color: "text-red-800 border-red-300 bg-red-50" },
];

const BOILERPLATE = "Catalyst BH Consulting is a Phoenix-based advisory firm serving behavioral-healthcare organizations across the U.S. The team pairs decades of frontline experience with a modern program-development platform to help operators open new programs, pass accreditation, hire and retain clinical staff, and grow revenue. Services include HR consulting, program startup, survey readiness, curriculum, and executive coaching. Learn more at https://catalystbhconsulting.com.";

export default function DirectorySubmissions() {
  const [statuses, setStatuses] = useState({}); // { url: {status, notes} }
  const [copied, setCopied] = useState(false);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/directories/status")
      .then((r) => setStatuses(r.data || {}))
      .catch(() => { /* first load — empty state */ });
  }, []);

  const persist = async (next) => {
    setStatuses(next);
    setSaving(true);
    try {
      await api.put("/admin/directories/status", next);
    } finally {
      setSaving(false);
    }
  };

  const setStatus = (url, status) => {
    persist({ ...statuses, [url]: { ...(statuses[url] || {}), status } });
  };

  const setNotes = (url, notes) => {
    persist({ ...statuses, [url]: { ...(statuses[url] || {}), notes } });
  };

  const copyBoilerplate = async () => {
    await navigator.clipboard.writeText(BOILERPLATE);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const filtered = DIRECTORIES.filter((d) => {
    const s = statuses[d.url]?.status || "not_started";
    if (filter !== "all" && filter !== s) return false;
    if (query && !`${d.name} ${d.category} ${d.notes}`.toLowerCase().includes(query.toLowerCase())) return false;
    return true;
  });

  const counts = STATUSES.reduce((acc, st) => {
    acc[st.key] = DIRECTORIES.filter((d) => (statuses[d.url]?.status || "not_started") === st.key).length;
    return acc;
  }, {});

  return (
    <div data-testid="directory-submissions">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-display text-2xl text-[var(--navy)]">Backlink & Directory Submissions</h2>
          <p className="text-xs text-[var(--muted)] mt-1">
            Curated list of high-authority directories. Submit yourself, then track status here.
          </p>
        </div>
        <button
          onClick={copyBoilerplate}
          className={`inline-flex items-center gap-2 border px-3 py-2 text-xs transition-colors ${
            copied ? "border-emerald-500 text-emerald-700 bg-emerald-50" : "border-[var(--line)] hover:bg-[var(--cream)]"
          }`}
          data-testid="copy-boilerplate"
        >
          {copied ? <><Check size={13} /> Copied</> : <><Copy size={13} /> Copy Boilerplate</>}
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-6" data-testid="directory-stats">
        {STATUSES.map((st) => (
          <button
            key={st.key}
            onClick={() => setFilter(st.key === filter ? "all" : st.key)}
            className={`border p-3 text-left transition-colors ${filter === st.key ? "bg-[var(--navy)] text-white" : "bg-white " + st.color}`}
            data-testid={`filter-${st.key}`}
          >
            <div className="text-[10px] tracking-widest uppercase font-bold flex items-center gap-1">{st.icon} {st.label}</div>
            <div className="font-display text-2xl mt-1">{counts[st.key] || 0}</div>
          </button>
        ))}
        <div className="border border-[var(--line)] p-3 flex items-center gap-2 bg-[var(--cream)]">
          <Search size={13} className="text-[var(--muted)]" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search…"
            className="flex-1 bg-transparent text-xs outline-none"
            data-testid="directory-search"
          />
        </div>
      </div>

      <ul className="space-y-2">
        {filtered.map((d) => {
          const rowStatus = statuses[d.url]?.status || "not_started";
          const rowStatusMeta = STATUSES.find((s) => s.key === rowStatus);
          return (
            <li key={d.url} className="border border-[var(--line)] bg-white p-4" data-testid={`dir-${d.name.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}>
              <div className="flex items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[9px] font-bold tracking-widest px-1.5 py-0.5 ${
                      d.priority === "P0" ? "bg-[var(--gold)] text-white"
                        : d.priority === "P1" ? "bg-[var(--navy)] text-white"
                        : "bg-[var(--line)] text-[var(--ink)]"
                    }`}>{d.priority}</span>
                    <a href={d.url} target="_blank" rel="noreferrer" className="font-display text-base text-[var(--navy)] hover:text-[var(--gold)] inline-flex items-center gap-1.5">
                      {d.name} <ExternalLink size={11} />
                    </a>
                    <span className="text-[10px] tracking-widest uppercase text-[var(--muted)]">{d.category}</span>
                  </div>
                  <p className="text-xs text-[var(--muted)] mt-1.5">{d.notes}</p>
                  {statuses[d.url]?.status && statuses[d.url]?.status !== "not_started" && (
                    <input
                      value={statuses[d.url]?.notes || ""}
                      onChange={(e) => setNotes(d.url, e.target.value)}
                      placeholder="Your notes (submission ID, contact, ETA…)"
                      className="w-full mt-2 px-2 py-1 border border-[var(--line)] text-xs bg-[#fafafa]"
                    />
                  )}
                </div>
                <select
                  value={rowStatus}
                  onChange={(e) => setStatus(d.url, e.target.value)}
                  className={`text-[11px] border px-2 py-1.5 font-bold uppercase tracking-wider ${rowStatusMeta.color}`}
                  data-testid={`status-${d.name.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}
                >
                  {STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
              </div>
            </li>
          );
        })}
      </ul>

      {saving && <div className="text-[10px] text-[var(--muted)] text-right mt-3 italic">saving…</div>}
    </div>
  );
}
